import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { API_BASE_URL, getHeaders } from '../utils/api';
import { emitSessionExpiredEvent, normalizeInactiveOrganizationError } from '../utils/adminUi';
import { showToast } from '../utils/toast';
import SearchableDropdown from './shared/SearchableDropdown';
import { trackEvent } from '../services/applicationInsights';
import {
  FaUsers, FaTimes,
  FaSearch, FaChevronLeft, FaChevronRight,
  FaCircle, FaUserShield, FaIdBadge, FaSyncAlt, FaTrash, FaQuestionCircle,
  FaCubes, FaHourglassHalf, FaCheckCircle, FaBan, FaInfoCircle,
  FaBuilding, FaCheck
} from 'react-icons/fa';

const parseResponseMessage = async (res) => {
  let text = '';
  let parsedBody = null;
  const textReadableResponse = typeof res?.clone === 'function' ? res.clone() : res;
  const jsonReadableResponse = typeof res?.clone === 'function' ? res.clone() : null;
  const originalJsonReadableResponse = typeof res?.json === 'function' ? res : null;

  try {
    text = await textReadableResponse.text();
  } catch {
    return '';
  }

  try {
    parsedBody = JSON.parse(text);
  } catch {
    parsedBody = null;
  }

  if (!parsedBody && typeof jsonReadableResponse?.json === 'function') {
    try {
      parsedBody = await jsonReadableResponse.json();
    } catch {
      parsedBody = null;
    }
  }

  if (!parsedBody && !text && typeof originalJsonReadableResponse?.json === 'function') {
    try {
      parsedBody = await originalJsonReadableResponse.json();
    } catch {
      parsedBody = null;
    }
  }

  if (parsedBody?.message) {
    return normalizeInactiveOrganizationError(res?.status, parsedBody.message, '');
  }

  if (Array.isArray(parsedBody?.errors) && parsedBody.errors.length > 0) {
    return normalizeInactiveOrganizationError(res?.status, parsedBody.errors.join(' '), '');
  }

  if (parsedBody?.title) {
    return normalizeInactiveOrganizationError(res?.status, parsedBody.title, '');
  }

  return normalizeInactiveOrganizationError(res?.status, text, '');
};

const parseActionResponse = async (res) => {
  const readableResponse = typeof res?.clone === 'function' ? res.clone() : res;

  try {
    return await readableResponse.json();
  } catch {
    return null;
  }
};

const mapActionError = (status, message, fallbackMessage) => {
  if (status === 400) return message || 'Please check inputs and try again.';
  if (status === 401) {
    emitSessionExpiredEvent();
    return message || 'Your session expired. Please sign in again.';
  }
  if (status === 403) return normalizeInactiveOrganizationError(status, message, 'You do not have permission for this action.');
  if (status === 404) return message || 'User or role not found.';
  if (status === 409) return message || 'This action conflicts with current state.';
  return message || fallbackMessage;
};

const SYSTEM_ORGANIZATION_NAME = 'systemorganization';

const USER_ACTION_ERROR_MESSAGES = {
  DELETE_USER: {
    notFound: 'User not found.',
    conflict: 'User cannot be deleted while role assignments exist.'
  }
};

const isSystemOrganizationValue = (value) =>
  String(value || '').trim().toLowerCase() === SYSTEM_ORGANIZATION_NAME;

const normalizeApprovalStatus = (status) => String(status || '').trim().toLowerCase();
const isApprovedUser = (user) => normalizeApprovalStatus(user?.approvalStatus) === 'approved';
const isPendingUser = (user) => normalizeApprovalStatus(user?.approvalStatus) === 'pending';
const isRejectedUser = (user) => normalizeApprovalStatus(user?.approvalStatus) === 'rejected';

export default function UsersManagement({ token, roles = [], orgId, initialUserEmail, initialFilterStatus, onLoadComplete }) {
  const tableRef = useRef(null);
  const fetchUsersAbortRef = useRef(null);
  const fetchSitesAbortRef = useRef(null);

  // Role checks - normalize roles to lowercase
  const normalizedRoles = useMemo(() => (Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : []), [roles]);
  const isSystemAdmin = normalizedRoles.includes("systemadministrator");
  const isOrgAdmin = normalizedRoles.includes("organizationadministrator");

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [siteCollections, setSiteCollections] = useState([]);
  const [siteCollectionNames, setSiteCollectionNames] = useState({});
  const [selectedOrg, setSelectedOrg] = useState(() => {
    const isSys = (Array.isArray(roles) ? roles : []).some(r => String(r).toLowerCase() === 'systemadministrator');
    return isSys ? '' : (orgId || '');
  });
  const [loading, setLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState({});
  const [selectedRoleSiteIds, setSelectedRoleSiteIds] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [roleAssignmentOptions, setRoleAssignmentOptions] = useState(null);
  const [loadingRoleOptions, setLoadingRoleOptions] = useState(false);

  // Drawer States
  const [activeUser, setActiveUser] = useState(null);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [expandedSiteId, setExpandedSiteId] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Can manage users - only system and organization admins
  const canManageUsers = isSystemAdmin || isOrgAdmin;
  const isActiveUserRejected = isRejectedUser(activeUser);

  // Keep non-system-admin synced with orgId prop if it updates
  useEffect(() => {
    if (!isSystemAdmin && orgId && selectedOrg !== orgId) {
      setSelectedOrg(orgId);
    }
  }, [isSystemAdmin, orgId, selectedOrg]);

  // Abort in-flight requests on unmount
  useEffect(() => {
    return () => {
      fetchUsersAbortRef.current?.abort();
      fetchSitesAbortRef.current?.abort();
    };
  }, []);

  // 1. Load Organizations
  useEffect(() => {
    if (isSystemAdmin && token) {
      fetch(`${API_BASE_URL}/organizations`, { headers: getHeaders(token) })
        .then(res => res.json())
        .then(data => {
          setOrganizations(data || []);
        })
        .catch(() => showToast.error('Failed to load organizations.'));
    }
  }, [isSystemAdmin, token]);

  // 2. Fetch Users & Sites
  const fetchUsers = useCallback(async (orgToFetch) => {
    if (!orgToFetch) return;

    fetchUsersAbortRef.current?.abort();
    const controller = new AbortController();
    fetchUsersAbortRef.current = controller;

    setLoading(true);
    setUsers([]);
    try {
      const res = await fetch(`${API_BASE_URL}/users/organizations/${orgToFetch}`, {
        headers: getHeaders(token),
        signal: controller.signal
      });

      if (!res.ok) {
        const message = await parseResponseMessage(res);
        throw new Error(mapActionError(res.status, message, 'Failed to refresh user directory.'));
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setUsers(list.map(u => ({ ...u, roleDetails: u.roleDetails || [] })));
    } catch (err) {
      if (err?.name === 'AbortError') return;
      showToast.error(err?.message || 'Failed to refresh user directory.');
      setUsers([]);
    } finally {
      if (fetchUsersAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [token]);

  const fetchSiteCollections = useCallback(async (orgToFetch) => {
    if (!orgToFetch) return;

    fetchSitesAbortRef.current?.abort();
    const controller = new AbortController();
    fetchSitesAbortRef.current = controller;

    try {
      const res = await fetch(`${API_BASE_URL}/site-collections?organizationId=${orgToFetch}`, {
        headers: getHeaders(token),
        signal: controller.signal
      });
      if (!res.ok) return;
      const data = await res.json();
      setSiteCollections(data || []);
      const mapping = {};
      (data || []).forEach(sc => mapping[sc.id] = sc.name);
      setSiteCollectionNames(mapping);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      // Handle error silently
    }
  }, [token]);

  const fetchData = useCallback(async (orgToFetch) => {
    await Promise.all([fetchSiteCollections(orgToFetch), fetchUsers(orgToFetch)]);
  }, [fetchSiteCollections, fetchUsers]);

  useEffect(() => {
    if (!selectedOrg) {
      fetchUsersAbortRef.current?.abort();
      fetchSitesAbortRef.current?.abort();
      setUsers([]);
      setSiteCollections([]);
      setSiteCollectionNames({});
      return;
    }
    fetchData(selectedOrg);
  }, [selectedOrg, token, fetchData]);

  // Auto-open drawer or set filter if initial params are provided
  useEffect(() => {
    if (initialFilterStatus) {
      setFilterStatus(initialFilterStatus);
      onLoadComplete?.();
    }
  }, [initialFilterStatus, onLoadComplete]);

  useEffect(() => {
    if (initialUserEmail && users.length > 0) {
      const normalizedInitialEmail = String(initialUserEmail || '').trim().toLowerCase();
      const targetUser = users.find(u => String(u.email || '').trim().toLowerCase() === normalizedInitialEmail);
      if (targetUser) {
        setActiveUser(targetUser);
        setConfirmation(null);
        setPermissionSearch('');
        setExpandedSiteId(null);

        setTimeout(() => {
          const element = document.getElementById(`user-row-${targetUser.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.style.backgroundColor = '#f0f9ff';
            setTimeout(() => { element.style.backgroundColor = ''; }, 2000);
          }
        }, 300);
      }
      onLoadComplete?.();
    }
  }, [initialUserEmail, users, onLoadComplete]);

  const filteredPermissions = useMemo(() => {
    if (!activeUser || !activeUser.roleDetails) return [];
    const term = permissionSearch.toLowerCase().trim();
    let result = [...activeUser.roleDetails];
    if (term) {
      result = result.filter(r => {
        const roleName = (r.name || '').toLowerCase();
        const siteName = (siteCollectionNames[r.siteCollectionId] || r.siteCollectionId || '').toLowerCase();
        return roleName.includes(term) || siteName.includes(term);
      });
    }
    return result.sort((a, b) => {
      const aIsSite = a.name === 'SiteCollectionOwner';
      const bIsSite = b.name === 'SiteCollectionOwner';
      if (!aIsSite && bIsSite) return -1;
      if (aIsSite && !bIsSite) return 1;
      return 0;
    });
  }, [activeUser, permissionSearch, siteCollectionNames]);

  const stats = useMemo(() => {
    const approvedUsers = users.filter(isApprovedUser);
    return {
      total: users.length,
      active: approvedUsers.filter(u => u.isActive).length,
      pending: users.filter(isPendingUser).length,
      inactive: approvedUsers.filter(u => !u.isActive).length,
      rejected: users.filter(isRejectedUser).length,
    };
  }, [users]);

  const handleKpiClick = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    let filtered = [...users];
    if (searchTerm) {
      filtered = filtered.filter(u =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus === 'All') {
      // Show the full directory, including rejected records, in the default view.
    }
    else if (filterStatus === 'Active') filtered = filtered.filter(u => u.isActive && isApprovedUser(u));
    else if (filterStatus === 'Pending') filtered = filtered.filter(isPendingUser);
    else if (filterStatus === 'Inactive') filtered = filtered.filter(u => !u.isActive && isApprovedUser(u));
    else if (filterStatus === 'Rejected') filtered = filtered.filter(isRejectedUser);

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm, filterStatus]);

  const executeAction = async () => {
    if (!confirmation) return;
    const { userId, type, role, siteId, roleName } = confirmation;
    setConfirmation(null);

    try {
      if (type === 'APPROVE_USER') {
        const headers = getHeaders(token);
        const url = `${API_BASE_URL}/users/${userId}/approve?organizationId=${selectedOrg}`;
        const res = await fetch(url, { method: 'PUT', headers });

        const payload = await parseActionResponse(res);

        if (!res.ok || payload?.success === false) {
          const message = payload?.message || await parseResponseMessage(res);
          throw new Error(mapActionError(res.status, message, 'Failed to approve user'));
        }
        
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, approvalStatus: 'Approved', isActive: true } : u));
        if (activeUser?.id === userId) setActiveUser(prev => ({ ...prev, approvalStatus: 'Approved', isActive: true }));
        showToast.success(payload?.message || 'User approved successfully');
      } else if (type === 'REJECT_USER') {
        const headers = getHeaders(token);
        const url = `${API_BASE_URL}/users/${userId}/reject?organizationId=${selectedOrg}`;
        const res = await fetch(url, { method: 'PUT', headers });
        const payload = await parseActionResponse(res);

        if (!res.ok || payload?.success === false) {
          const message = payload?.message || await parseResponseMessage(res);
          throw new Error(mapActionError(res.status, message, 'Failed to reject user'));
        }

        setUsers(prev => prev.map(u => u.id === userId ? { ...u, approvalStatus: 'Rejected', isActive: false } : u));
        if (activeUser?.id === userId) {
          setActiveUser(prev => ({ ...prev, approvalStatus: 'Rejected', isActive: false }));
        }
        setSelectedRoles(prev => {
          const next = { ...prev };
          delete next[userId];
          return next;
        });
        setSelectedRoleSiteIds(prev => {
          const next = { ...prev };
          delete next[userId];
          return next;
        });
        setRoleAssignmentOptions(null);
        showToast.success(payload?.message || 'User rejected successfully');
      } else if (type === 'TOGGLE_STATUS') {
        const user = users.find(u => u.id === userId);
        if (!user) {
          throw new Error('User no longer exists in the current directory.');
        }

        if (isRejectedUser(user)) {
          throw new Error('Rejected users cannot be re-activated.');
        }

        const newStatus = !user.isActive;
        const res = await fetch(`${API_BASE_URL}/users/${userId}/activestatus/${newStatus}?organizationId=${selectedOrg}`, { method: 'PUT', headers: getHeaders(token) });
        const payload = await parseActionResponse(res);

        if (!res.ok || payload?.success === false) {
          const message = payload?.message || await parseResponseMessage(res);
          throw new Error(mapActionError(res.status, message, 'Failed to update status'));
        }
        
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, isActive: newStatus } : u));
        if (activeUser?.id === userId) setActiveUser(prev => ({ ...prev, isActive: newStatus }));
        showToast.success(payload?.message || `User active status set to ${newStatus} successfully`);
      }
      else if (type === 'REVOKE_ROLE') {
        if (role?.name === 'SiteCollectionOwner' && !role?.siteCollectionId) {
          showToast.error('siteCollectionId is required when removing SiteCollectionOwner role.');
          return;
        }

        let url = `${API_BASE_URL}/users/${userId}/remove-role/${encodeURIComponent(role.name)}?organizationId=${selectedOrg}`;
        if (role.siteCollectionId) url += `&siteCollectionId=${role.siteCollectionId}`;

        const res = await fetch(url, {
          method: 'DELETE',
          headers: getHeaders(token)
        });

        if (!res.ok) {
          const message = await parseResponseMessage(res);
          throw new Error(mapActionError(res.status, message, 'Something went wrong while updating roles. Please try again.'));
        }

        const payload = await parseActionResponse(res);
        if (payload?.success === false) {
          throw new Error(payload?.message || 'Failed to remove role.');
        }
        
        const updatedRoles = activeUser.roleDetails.filter(r => !(r.name === role.name && r.siteCollectionId === role.siteCollectionId));
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, roleDetails: updatedRoles } : u));
        if (activeUser?.id === userId) setActiveUser(prev => ({ ...prev, roleDetails: updatedRoles }));
        showToast.success(payload?.message || 'Permission revoked successfully.');
        await fetchRoleAssignmentOptions(userId);
      }
      else if (type === 'ASSIGN_ROLE') {
        if (isActiveUserRejected) {
          showToast.error('Rejected users cannot receive new role assignments.');
          return;
        }

        if (roleName === 'SystemAdministrator' && !isActiveUserInSystemOrganization) {
          showToast.error('SystemAdministrator role can only be assigned to users in SystemOrganization.');
          return;
        }

        let url = `${API_BASE_URL}/users/${userId}/assign-role/${encodeURIComponent(roleName)}?organizationId=${selectedOrg}`;
        if (siteId) url += `&siteCollectionId=${siteId}`;

        const res = await fetch(url, {
          method: 'POST',
          headers: getHeaders(token)
        });

        if (!res.ok) {
          const message = await parseResponseMessage(res);
          throw new Error(mapActionError(res.status, message, 'Something went wrong while updating roles. Please try again.'));
        }

        const payload = await parseActionResponse(res);
        if (payload?.success === false) {
          throw new Error(payload?.message || 'Failed to assign role.');
        }
        
        const newRole = { name: roleName, siteCollectionId: siteId };
        const updatedRoles = [...(activeUser.roleDetails || []), newRole];
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, roleDetails: updatedRoles } : u));
        if (activeUser?.id === userId) setActiveUser(prev => ({ ...prev, roleDetails: updatedRoles }));
        showToast.success(payload?.message || `"${roleName}" role granted successfully.`);
        setSelectedRoles(p => ({ ...p, [userId]: '' }));
        setSelectedRoleSiteIds(p => ({ ...p, [userId]: '' }));
        await fetchRoleAssignmentOptions(userId);
      }
      else if (type === 'DELETE_USER') {
        if (!userId) {
          throw new Error('Invalid user ID format.');
        }

        const res = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: getHeaders(token)
        });

        const payload = await parseActionResponse(res);

        if (!res.ok || payload?.success === false) {
          const message = payload?.message || await parseResponseMessage(res);
          const fallback = 'Failed to delete user.';

          if (res.status === 404) {
            throw new Error(message || USER_ACTION_ERROR_MESSAGES.DELETE_USER.notFound);
          }

          if (res.status === 409) {
            throw new Error(message || USER_ACTION_ERROR_MESSAGES.DELETE_USER.conflict);
          }

          throw new Error(mapActionError(res.status, message, fallback));
        }

        setUsers(prev => prev.filter(u => u.id !== userId));
        setSelectedRoles(prev => {
          const next = { ...prev };
          delete next[userId];
          return next;
        });
        setSelectedRoleSiteIds(prev => {
          const next = { ...prev };
          delete next[userId];
          return next;
        });
        setRoleAssignmentOptions(null);
        setActiveUser(null);
        showToast.success(payload?.message || 'User deleted successfully.');
      }
    } catch (err) {
      showToast.error(err.message || 'An unexpected error occurred.');
    }
  };

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage]);

  const syncDirectory = async () => {
    setLoading(true);
    try {
      await fetchData(selectedOrg);
      showToast.success('Directory synchronized successfully.');
    } catch (err) {
      showToast.error('Sync failed.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleAssignmentOptions = useCallback(async (targetUserId) => {
    if (!token || !targetUserId) return;
    setLoadingRoleOptions(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/role-assignment-options?targetUserId=${encodeURIComponent(targetUserId)}`, {
        headers: getHeaders(token)
      });

      if (!res.ok) {
        const message = await parseResponseMessage(res);
        throw new Error(message || 'Failed to load role assignment options.');
      }

      const options = await res.json();
      setRoleAssignmentOptions(options || null);
    } catch (err) {
      setRoleAssignmentOptions(null);
      showToast.error(err?.message || 'Failed to load role assignment options.');
    } finally {
      setLoadingRoleOptions(false);
    }
  }, [token]);

  useEffect(() => {
    if (!activeUser || !canManageUsers) {
      setRoleAssignmentOptions(null);
      return;
    }

    if (isRejectedUser(activeUser)) {
      setRoleAssignmentOptions(null);
      return;
    }

    fetchRoleAssignmentOptions(activeUser.id);
  }, [activeUser, canManageUsers, fetchRoleAssignmentOptions]);

  const isActiveUserInSystemOrganization = useMemo(() => {
    if (!activeUser) return false;

    const directNameMatches = [
      activeUser.organizationName,
      activeUser.organizationDisplayName,
      activeUser.organization?.name,
      typeof activeUser.organization === 'string' ? activeUser.organization : null
    ].some(isSystemOrganizationValue);

    if (directNameMatches) return true;

    const selectedOrganization = organizations.find(org => String(org.id) === String(selectedOrg));
    const activeUserOrganizationMatchesSelection =
      activeUser.organizationId && selectedOrg && String(activeUser.organizationId) === String(selectedOrg);

    if (activeUserOrganizationMatchesSelection && selectedOrganization) {
      return isSystemOrganizationValue(selectedOrganization.name);
    }

    return false;
  }, [activeUser, organizations, selectedOrg]);

  const availableRoleOptions = useMemo(() => {
    if (!activeUser || isRejectedUser(activeUser) || !roleAssignmentOptions?.supportedRoles) return [];

    const currentRoles = activeUser.roleDetails || [];
    const assignableRoleNames = new Set(roleAssignmentOptions.assignableRoleNames || []);

    return roleAssignmentOptions.supportedRoles
      .filter(r => r.assignable)
      .filter(r => assignableRoleNames.size === 0 || assignableRoleNames.has(r.name))
      .filter(r => r.name !== 'SystemAdministrator' || isActiveUserInSystemOrganization)
      .filter(r => {
        if (r.requiresSiteCollectionId) return true;
        return !currentRoles.some(cr => cr.name === r.name);
      })
      .map(r => ({
        id: r.name,
        name: r.name,
        description: r.description,
        requiresSiteCollectionId: r.requiresSiteCollectionId
      }));
  }, [activeUser, isActiveUserInSystemOrganization, roleAssignmentOptions]);

  const selectedRoleOption = useMemo(() => {
    if (!activeUser) return null;
    const selectedRoleName = selectedRoles[activeUser.id] || '';
    return availableRoleOptions.find(r => r.id === selectedRoleName) || null;
  }, [activeUser, availableRoleOptions, selectedRoles]);

  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>

        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}><FaUsers size={22} /></div>
            <div>
              <h1 style={titleStyle}>Member Directory</h1>
              <p style={subtitleStyle}>
                {isSystemAdmin && !selectedOrg
                  ? 'Select an organization to view its members'
                  : canManageUsers 
                    ? `Managing ${stats.total} organization members`
                    : `Viewing ${stats.total} organization members`
                }
              </p>
            </div>
          </div>
        </header>

        {isSystemAdmin && (
          <div style={filtersCardStyle}>
            <div style={cardHeaderStyle}>
              <FaBuilding color="#6366f1" size={14} />
              <span style={cardTitleStyle}>Organization Context</span>
            </div>
            <div style={filtersGridStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Select Organization</label>
                <SearchableDropdown
                  options={organizations}
                  selectedValue={selectedOrg}
                  onSelect={(value) => {
                    setSelectedOrg(value);
                    if (value) {
                      trackEvent('Dropdown Selected', {
                        section: 'users',
                        organizationId: value
                      });
                    }
                  }}
                  placeholder="Choose Org"
                  displayKey="name"
                  valueKey="id"
                  styles={{
                    container: dropdownContainerStyle,
                    trigger: dropdownTriggerStyle,
                    triggerText: dropdownTriggerTextStyle,
                    menu: dropdownMenuStyle,
                    searchContainer: searchBoxContainerStyle,
                    searchIcon: searchIconStyle,
                    searchInput: dropdownSearchInputStyle,
                    list: dropdownListStyle,
                    option: dropdownOptionStyle,
                    optionText: optionTextStyle,
                    optionName: optionNameStyle,
                    optionId: optionIdStyle,
                    emptyOption: emptyOptionStyle
                  }}
                />
              </div>
              <button style={primaryButtonStyle} onClick={syncDirectory} disabled={loading || !selectedOrg}>
                <FaSyncAlt className={loading ? 'spin' : ''} /> {loading ? 'Syncing...' : 'Sync Users'}
              </button>
            </div>
          </div>
        )}

        <div style={kpiRowStyle}>
          <div className="kpi-card" style={filterStatus === 'All' ? kpiCardActiveAll : kpiCardWhite} onClick={() => handleKpiClick('All')}>
            <div style={kpiHeader}><div style={{...kpiIcon, color: '#6366f1', background: '#f5f3ff'}}><FaCubes /></div><span style={kpiLabel}>Total Members</span></div>
            <div style={kpiValue}>{stats.total}</div>
            <div style={kpiMeta}>Directory Members</div>
          </div>

          <div className="kpi-card" style={filterStatus === 'Pending' ? kpiCardActivePending : kpiCardWhite} onClick={() => handleKpiClick('Pending')}>
            <div style={kpiHeader}><div style={{ ...kpiIcon, background: '#fffbeb', color: '#d97706' }}><FaHourglassHalf /></div><span style={kpiLabel}>Pending</span></div>
            <div style={{ ...kpiValue, color: '#f59e0b' }}>{stats.pending}</div>
            <div style={kpiMeta}>Awaiting Review</div>
          </div>

          <div className="kpi-card" style={filterStatus === 'Active' ? kpiCardActiveActive : kpiCardWhite} onClick={() => handleKpiClick('Active')}>
            <div style={kpiHeader}><div style={{ ...kpiIcon, background: '#f0fdf4', color: '#16a34a' }}><FaCheckCircle /></div><span style={kpiLabel}>Active</span></div>
            <div style={{ ...kpiValue, color: '#10b981' }}>{stats.active}</div>
            <div style={kpiMeta}>Active Members</div>
          </div>

          <div className="kpi-card" style={filterStatus === 'Inactive' ? kpiCardActiveInactive : kpiCardWhite} onClick={() => handleKpiClick('Inactive')}>
            <div style={kpiHeader}><div style={{ ...kpiIcon, background: '#fef2f2', color: '#dc2626' }}><FaBan /></div><span style={kpiLabel}>Disabled</span></div>
            <div style={{ ...kpiValue, color: '#ef4444' }}>{stats.inactive}</div>
            <div style={kpiMeta}>Inactive Members</div>
          </div>

          <div className="kpi-card" style={filterStatus === 'Rejected' ? kpiCardActiveRejected : kpiCardWhite} onClick={() => handleKpiClick('Rejected')}>
            <div style={kpiHeader}><div style={{ ...kpiIcon, background: '#fef2f2', color: '#b91c1c' }}><FaTimes /></div><span style={kpiLabel}>Rejected</span></div>
            <div style={{ ...kpiValue, color: '#dc2626' }}>{stats.rejected}</div>
            <div style={kpiMeta}>Rejected Members</div>
          </div>
        </div>

        <div ref={tableRef} style={tableCardStyle}>
          <div style={toolbarStyle}>
            <div style={searchWrapper}>
              <FaSearch style={searchIcon} />
              <input style={searchInput} placeholder="Search members by name or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>

          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderRowStyle}>
                <th style={thStyle}>Member Information</th>
                <th style={thStyle}>Directory Status</th>
                {canManageUsers && <th style={thActionStyle}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canManageUsers ? "3" : "2"} style={emptyStateStyle}><FaSyncAlt className="spin" /> Syncing...</td></tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={canManageUsers ? "3" : "2"} style={emptyStateStyle}>
                    {isSystemAdmin && !selectedOrg
                      ? 'Please select an organization to view directory members.'
                      : 'No members match your criteria.'}
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr key={user.id} id={`user-row-${user.id}`} style={trStyle}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={avatarStyle}>{user.firstName?.[0]}</div>
                        <div>
                          <div style={userNameText}>{user.firstName} {user.lastName}</div>
                          <div style={userEmailText}>{user.email}</div>
                          <div style={userIdText}>ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={getBadgeStyle(user)}>
                        <FaCircle size={6} />
                        {isPendingUser(user)
                          ? 'Pending Approval'
                          : isRejectedUser(user)
                            ? 'Rejected'
                            : (user.isActive ? 'Active' : 'Inactive')}
                      </span>
                    </td>
                    {canManageUsers && (
                      <td style={tdActionStyle}>
                        {isPendingUser(user) ? (
                          <>
                            {confirmation?.userId === user.id && (confirmation.type === 'APPROVE_USER' || confirmation.type === 'REJECT_USER') ? (
                              <div style={inlineConfirmWrapper}>
                                <FaQuestionCircle color="#6366f1" size={14} />
                                <span style={confirmText}>Confirm {confirmation.type === 'APPROVE_USER' ? 'Approve' : 'Reject'}?</span>
                                <button style={btnYes} onClick={executeAction}>Yes</button>
                                <button style={btnNo} onClick={() => setConfirmation(null)}>No</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button style={viewDetailsBtnStyle} onClick={() => setActiveUser(user)} title="View user details">
                                  <FaInfoCircle size={10} />
                                </button>
                                <button style={actionBtnApprove} onClick={() => setConfirmation({ userId: user.id, type: 'APPROVE_USER' })} title="Approve this user">
                                  <FaCheck size={12} />
                                </button>
                                <button style={actionBtnReject} onClick={() => setConfirmation({ userId: user.id, type: 'REJECT_USER' })} title="Reject this user">
                                  <FaTimes size={12} />
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <button style={manageBtn} onClick={() => { setActiveUser(user); setConfirmation(null); setPermissionSearch(''); setExpandedSiteId(null); }}>
                            Manage Access <FaChevronRight size={10} style={{ marginLeft: 6 }} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {filteredUsers.length > itemsPerPage && (
            <div style={paginationRow}>
              <div style={paginationInfoStyle}>Showing <b>{paginatedUsers.length}</b> members</div>
              <div style={paginationControlsWrapper}>
                <button style={pageArrow} disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><FaChevronLeft /></button>
                <button style={pageArrow} disabled={currentPage === Math.ceil(filteredUsers.length / itemsPerPage)} onClick={() => setCurrentPage(p => p + 1)}><FaChevronRight /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DRAWER - Only for users with management permissions */}
      {activeUser && canManageUsers && (
        <div style={drawerOverlay} onClick={() => setActiveUser(null)}>
          <div style={drawerContent} onClick={e => e.stopPropagation()}>
            <div style={drawerHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={drawerAvatar}>{activeUser.firstName?.[0]}</div>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{activeUser.firstName} {activeUser.lastName}</h2>
                  <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{activeUser.email}</p>
                </div>
              </div>
              <button style={drawerCloseBtn} onClick={() => setActiveUser(null)} title="Close Panel">
                <FaTimes />
              </button>
            </div>

            <div style={drawerBody}>
              {isPendingUser(activeUser) && (
                <div style={{ ...pendingBannerStyle, marginBottom: 24 }}>
                  <FaHourglassHalf style={{ fontSize: 16, color: '#d97706' }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Pending Approval</div>
                    <div style={{ fontSize: 12, color: '#92400e' }}>This user is awaiting approval to access the organization</div>
                  </div>
                </div>
              )}

              <div style={drawerSection}>
                <h3 style={sectionTitle}><FaInfoCircle /> User Information</h3>
                <div style={controlCard}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={detailLabelStyle}>First Name</span>
                      <span style={detailValueStyle}>{activeUser.firstName}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={detailLabelStyle}>Last Name</span>
                      <span style={detailValueStyle}>{activeUser.lastName}</span>
                    </div>
                    <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={detailLabelStyle}>Email</span>
                      <span style={detailValueStyle}>{activeUser.email}</span>
                    </div>
                  </div>
                </div>
              </div>

              {!isPendingUser(activeUser) && (
                <div style={drawerSection}>
                  <h3 style={sectionTitle}><FaIdBadge /> Member Status</h3>
                  <div style={controlCard}>
                    {isActiveUserRejected ? (
                      <div style={warningCardStyle}>
                        Rejected users cannot be re-activated.
                      </div>
                    ) : confirmation?.type === 'TOGGLE_STATUS' ? (
                      <div style={inlineConfirmWrapper}>
                        <FaQuestionCircle color="#6366f1" size={18} />
                        <span style={confirmLabel}>Confirm change?</span>
                        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                          <button style={btnYes} onClick={executeAction}>Yes</button>
                          <button style={btnNo} onClick={() => setConfirmation(null)}>No</button>
                        </div>
                      </div>
                    ) : (
                      <div style={statusRow}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>Login Permission</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>Status: {activeUser.isActive ? 'Active' : 'Inactive'}</div>
                        </div>
                        <div>
                          <button 
                            onClick={() => setConfirmation({ userId: activeUser.id, type: 'TOGGLE_STATUS' })} 
                            style={{ ...statusBtn, background: activeUser.isActive ? '#fee2e2' : '#dcfce7', color: activeUser.isActive ? '#991b1b' : '#166534' }}
                          >
                            {activeUser.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!isPendingUser(activeUser) && (
                <>
                  <div style={drawerSection}>
                    <h3 style={sectionTitle}><FaUserShield /> Provision New Role</h3>
                    <div style={controlCard}>
                      {loadingRoleOptions ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#64748b' }}>
                          <FaSyncAlt className="spin" /> Loading role options...
                        </div>
                      ) : isActiveUserRejected ? (
                        <div style={warningCardStyle}>
                          Rejected users cannot receive new role assignments.
                        </div>
                      ) : roleAssignmentOptions?.canManageTargetUser === false ? (
                        <div style={{ fontSize: 12, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 10 }}>
                          You do not have permission to modify roles for this user.
                        </div>
                      ) : (
                      <>
                      {confirmation?.type === 'ASSIGN_ROLE' ? (
                        <div style={inlineConfirmWrapper}>
                          <FaQuestionCircle color="#6366f1" size={18} />
                          <span style={confirmLabel}>Grant "{confirmation.roleName}"?</span>
                          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                            <button style={btnYes} onClick={executeAction}>Confirm</button>
                            <button style={btnNo} onClick={() => setConfirmation(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <SearchableDropdown
                            options={availableRoleOptions}
                            selectedValue={selectedRoles[activeUser.id] || ''}
                            onSelect={(val) => {
                              setSelectedRoles(p => ({ ...p, [activeUser.id]: val }));
                              setSelectedRoleSiteIds(p => ({ ...p, [activeUser.id]: '' }));
                            }}
                            placeholder="Choose Role..."
                            disabled={availableRoleOptions.length === 0}
                            styles={{
                              container: dropdownContainerStyle,
                              trigger: dropdownTriggerStyle,
                              triggerText: dropdownTriggerTextStyle,
                              menu: dropdownMenuStyle,
                              searchContainer: searchBoxContainerStyle,
                              searchIcon: searchIconStyle,
                              searchInput: dropdownSearchInputStyle,
                              list: dropdownListStyle,
                              option: dropdownOptionStyle,
                              optionText: optionTextStyle,
                              optionName: optionNameStyle,
                              optionId: optionIdStyle,
                              emptyOption: emptyOptionStyle
                            }}
                          />

                          {selectedRoleOption && !selectedRoleOption.requiresSiteCollectionId && (
                            <button
                              style={btnYes}
                              onClick={() => setConfirmation({ userId: activeUser.id, type: 'ASSIGN_ROLE', roleName: selectedRoleOption.id })}
                            >
                              Assign {selectedRoleOption.name}
                            </button>
                          )}

                          {selectedRoleOption?.requiresSiteCollectionId && (
                            <SearchableDropdown
                              options={siteCollections}
                              selectedValue={selectedRoleSiteIds[activeUser.id] || ''}
                              onSelect={(val) => setSelectedRoleSiteIds(p => ({ ...p, [activeUser.id]: val }))}
                              placeholder="Select Target Site Collection..."
                              styles={{
                                container: dropdownContainerStyle,
                                trigger: dropdownTriggerStyle,
                                triggerText: dropdownTriggerTextStyle,
                                menu: dropdownMenuStyle,
                                searchContainer: searchBoxContainerStyle,
                                searchIcon: searchIconStyle,
                                searchInput: dropdownSearchInputStyle,
                                list: dropdownListStyle,
                                option: dropdownOptionStyle,
                                optionText: optionTextStyle,
                                optionName: optionNameStyle,
                                optionId: optionIdStyle,
                                emptyOption: emptyOptionStyle
                              }}
                            />
                          )}

                          {selectedRoleOption?.requiresSiteCollectionId && (
                            <button
                              style={btnYes}
                              disabled={!selectedRoleSiteIds[activeUser.id]}
                              onClick={() => setConfirmation({
                                userId: activeUser.id,
                                type: 'ASSIGN_ROLE',
                                roleName: selectedRoleOption.id,
                                siteId: selectedRoleSiteIds[activeUser.id]
                              })}
                            >
                              Assign {selectedRoleOption.name}
                            </button>
                          )}

                          {!selectedRoleOption && (
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                              {availableRoleOptions.length === 0 ? 'No assignable roles available for this user.' : 'Choose a role to continue.'}
                            </div>
                          )}
                        </div>
                      )}
                      </>
                      )}
                    </div>
                  </div>

                  <div style={drawerSection}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <h3 style={{ ...sectionTitle, margin: 0 }}>Assigned Permissions</h3>
                      <div style={drawerSearchWrapper}>
                        <FaSearch size={10} style={{ marginRight: 6, color: '#94a3b8' }} />
                        <input style={drawerSearchInput} placeholder="Filter..." value={permissionSearch} onChange={e => setPermissionSearch(e.target.value)} />
                      </div>
                    </div>
                    <div style={permissionsContainer}>
                      {filteredPermissions.length === 0 ? (
                        <div style={emptyPermissions}>No active permissions.</div>
                      ) : (
                        filteredPermissions.map((role, idx) => (
                          <div key={idx} style={permissionCard}>
                            {confirmation?.type === 'REVOKE_ROLE' && confirmation.role === role ? (
                              <div style={inlineConfirmWrapper}>
                                <FaQuestionCircle color="#ef4444" />
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>Revoke access?</span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                                  <button style={{ ...btnYes, background: '#ef4444' }} onClick={executeAction}>Revoke</button>
                                  <button style={btnNo} onClick={() => setConfirmation(null)}>No</button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={roleNameTag}>{role.name === 'SiteCollectionOwner' ? 'Site Owner' : role.name}</div>
                                    {role.siteCollectionId && (
                                      <div style={siteMainLabel}>{siteCollectionNames[role.siteCollectionId] || 'Unknown site collection'}</div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {role.siteCollectionId && (
                                      <button style={detailBtn} onClick={() => setExpandedSiteId(expandedSiteId === role.siteCollectionId ? null : role.siteCollectionId)}>
                                        {expandedSiteId === role.siteCollectionId ? 'Less' : 'More'}
                                      </button>
                                    )}
                                    <button style={revokeBtn} onClick={() => setConfirmation({ userId: activeUser.id, type: 'REVOKE_ROLE', role })} title="Revoke Role">
                                      <FaTrash size={12} />
                                    </button>
                                  </div>
                                </div>
                                {role.siteCollectionId && expandedSiteId === role.siteCollectionId && (
                                  <div style={siteDetailBox}>
                                    <div style={detailItem}><b>Site ID:</b> <span>{role.siteCollectionId}</span></div>
                                    <div style={detailItem}><b>Site Name:</b> <span>{siteCollectionNames[role.siteCollectionId] || 'Unknown site collection'}</span></div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div style={drawerSection}>
                    <h3 style={sectionTitle}><FaTrash /> Remove User</h3>
                    <div style={controlCard}>
                      {confirmation?.type === 'DELETE_USER' ? (
                        <div style={inlineConfirmWrapper}>
                          <FaQuestionCircle color="#ef4444" size={18} />
                          <span style={confirmLabel}>Delete this user?</span>
                          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                            <button style={{ ...btnYes, background: '#ef4444' }} onClick={executeAction}>Delete</button>
                            <button style={btnNo} onClick={() => setConfirmation(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            User deletion is allowed only when no role assignments remain.
                          </div>
                          <button
                            style={{
                              ...deleteUserBtnStyle,
                              opacity: (activeUser.roleDetails || []).length > 0 ? 0.55 : 1,
                              cursor: (activeUser.roleDetails || []).length > 0 ? 'not-allowed' : 'pointer'
                            }}
                            disabled={(activeUser.roleDetails || []).length > 0}
                            onClick={() => setConfirmation({ userId: activeUser.id, type: 'DELETE_USER' })}
                          >
                            Delete User
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {isPendingUser(activeUser) && (
                <div style={drawerSection}>
                  <h3 style={sectionTitle}><FaCheckCircle /> Approval Actions</h3>
                  {confirmation?.userId === activeUser.id && (confirmation.type === 'APPROVE_USER' || confirmation.type === 'REJECT_USER') ? (
                    <div style={controlCard}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <div style={{
                          ...confirmIconStyle,
                          background: confirmation.type === 'APPROVE_USER' ? '#dcfce7' : '#fee2e2',
                          width: 40, height: 40, margin: 0
                        }}>
                          {confirmation.type === 'APPROVE_USER' ? <FaCheck style={{ fontSize: 20, color: '#16a34a' }} /> : <FaTimes style={{ fontSize: 20, color: '#dc2626' }} />}
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                            {confirmation.type === 'APPROVE_USER' ? 'Approve User?' : 'Reject User?'}
                          </h4>
                          <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#64748b' }}>
                            {confirmation.type === 'APPROVE_USER' ? 'Grant access and set status to Active' : 'Mark user as rejected and keep account inactive'}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={executeAction} style={{ ...btnYes, background: confirmation.type === 'APPROVE_USER' ? '#16a34a' : '#dc2626', flex: 1 }}>
                          {confirmation.type === 'APPROVE_USER' ? 'Approve' : 'Reject'}
                        </button>
                        <button onClick={() => setConfirmation(null)} style={{ ...btnNo, flex: 1 }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
                      <button onClick={() => setConfirmation({ userId: activeUser.id, type: 'APPROVE_USER' })} style={{ ...approveBtnLargeStyle, background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0' }}>
                        <FaCheck size={14} style={{ marginRight: 8 }} /> Approve User
                      </button>
                      <button onClick={() => setConfirmation({ userId: activeUser.id, type: 'REJECT_USER' })} style={{ ...approveBtnLargeStyle, background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }}>
                        <FaTimes size={14} style={{ marginRight: 8 }} /> Reject User
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideLeft { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .spin { animation: spin 2s infinite linear; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .kpi-card { transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); position: relative; overflow: hidden; cursor: pointer; }
        .kpi-card:hover { transform: translateY(-8px) scale(1.02); box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important; }
        .kpi-card:active { transform: translateY(-2px) scale(0.98); }
      `}</style>
    </div>
  );
}
/* ---------- DROPDOWN STYLES ---------- */
const dropdownContainerStyle = { position: 'relative', width: '100%' };
const dropdownTriggerStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontWeight: 500, color: '#1e293b', transition: 'all 0.2s ease', outline: 'none' };
const dropdownTriggerTextStyle = { flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const dropdownMenuStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, marginTop: 4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', zIndex: 1000, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' };
const searchBoxContainerStyle = { position: 'relative', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', background: '#fcfdfe' };
const searchIconStyle = { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12, pointerEvents: 'none' };
const dropdownSearchInputStyle = { width: '100%', padding: '8px 12px 8px 28px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' };
const dropdownListStyle = { maxHeight: '200px', overflowY: 'auto', padding: '4px' };
const dropdownOptionStyle = { width: '100%', padding: '10px 12px', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 6, margin: '2px 0', transition: 'all 0.2s ease', fontSize: 13 };
const optionTextStyle = { flex: 1 };
const optionNameStyle = { fontWeight: 600, color: 'inherit' };
const optionIdStyle = { fontSize: '11px', opacity: 0.6, marginTop: 2 };
const emptyOptionStyle = { padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 13 };

/* ---------- MAIN STYLES ---------- */
const filtersCardStyle = { background: '#fff', borderRadius: 16, padding: 24, marginBottom: 24, border: '1px solid #e2e8f0' };
const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' };
const cardTitleStyle = { fontSize: 15, fontWeight: 700, color: '#1e293b' };
const filtersGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, alignItems: 'end' };
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' };
const primaryButtonStyle = { background: '#6366f1', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', fontSize: 14 };

const wrapperStyle = { width: '100%', minHeight: '100vh', background: '#f8fafc', paddingBottom: 60, fontFamily: 'Inter, sans-serif' };
const containerStyle = { maxWidth: 1150, margin: '0 auto', padding: '24px', animation: 'fadeIn 0.5s ease-out' };
const headerStyle = { marginBottom: 32 };
const headerContentStyle = { display: 'flex', alignItems: 'center', gap: 16 };
const headerBadgeStyle = { width: 48, height: 48, borderRadius: 14, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1', boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.1)' };
const titleStyle = { fontSize: 24, fontWeight: 800, margin: 0, color: '#0f172a' };
const subtitleStyle = { fontSize: 14, color: '#64748b', marginTop: 4 };

const kpiRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20, marginBottom: 32 };
const kpiCardBase = { padding: 24, borderRadius: 24, transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', border: '1px solid #e2e8f0', background: '#fff' };
const kpiCardWhite = { ...kpiCardBase, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' };
const kpiCardActiveAll = { ...kpiCardBase, border: '2px solid #6366f1', background: '#f5f3ff', boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.1)' };
const kpiCardActivePending = { ...kpiCardBase, border: '2px solid #f59e0b', background: '#fffbeb', boxShadow: '0 10px 15px -3px rgba(245, 158, 11, 0.1)' };
const kpiCardActiveActive = { ...kpiCardBase, border: '2px solid #10b981', background: '#f0fdf4', boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.1)' };
const kpiCardActiveInactive = { ...kpiCardBase, border: '2px solid #ef4444', background: '#fef2f2', boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.1)' };
const kpiCardActiveRejected = { ...kpiCardBase, border: '2px solid #dc2626', background: '#fef2f2', boxShadow: '0 10px 15px -3px rgba(220, 38, 38, 0.1)' };
const kpiHeader = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 };
const kpiIcon = { width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const kpiLabel = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' };
const kpiValue = { fontSize: 32, fontWeight: 800, color: '#1e293b' };
const kpiMeta = { fontSize: 11, color: '#94a3b8' };

const tableCardStyle = { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' };
const toolbarStyle = { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const searchWrapper = { position: 'relative', width: '300px' };
const searchIcon = { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

const searchInput = { 
  width: '100%', 
  padding: '9px 12px 9px 38px', 
  borderRadius: 10, 
  border: '1.5px solid #e2e8f0', 
  fontSize: 13, 
  outline: 'none',
  background: '#fff',
  color: '#1e293b'
};

const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const tableHeaderRowStyle = { background: '#f8fafc' };
const thStyle = { padding: '16px 20px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left' };
const thActionStyle = { ...thStyle, textAlign: 'right' };
const trStyle = { borderBottom: '1px solid #f1f5f9' };
const tdStyle = { padding: '16px 20px', verticalAlign: 'middle' };
const tdActionStyle = { padding: '16px 20px', textAlign: 'right' };
const avatarStyle = { width: 38, height: 38, borderRadius: 10, background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 };
const userNameText = { fontWeight: 700, color: '#1e293b', fontSize: 14 };
const userEmailText = { fontSize: 12, color: '#64748b', marginTop: 2 };
const userIdText = { fontSize: 10, color: '#94a3b8', marginTop: 2 };
const manageBtn = { background: '#f8fafc', border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#6366f1', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' };
const viewDetailsBtnStyle = { background: '#e0f2fe', border: '1px solid #bae6fd', padding: '8px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#0369a1', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' };
const actionBtnApprove = { background: '#dcfce7', border: '1px solid #bbf7d0', padding: '8px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#16a34a', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' };
const actionBtnReject = { background: '#fee2e2', border: '1px solid #fecaca', padding: '8px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#dc2626', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' };
const emptyStateStyle = { padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 14 };

const paginationRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#fff', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 16 };
const paginationInfoStyle = { fontSize: 14, color: '#64748b', fontWeight: 500 };
const paginationControlsWrapper = { display: 'flex', alignItems: 'center', gap: 8 };
const pageNumberBtn = { minWidth: 36, height: 36, padding: '0 4px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: '700', cursor: 'pointer', color: '#64748b' };
const pageArrow = { ...pageNumberBtn, display: 'flex', alignItems: 'center', justifyContent: 'center' };

const drawerOverlay = { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 };
const drawerContent = { width: '100%', maxWidth: 460, background: '#fff', height: '100%', boxShadow: '-10px 0 30px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', animation: 'slideLeft 0.3s ease-out' };
const drawerHeader = { padding: '24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' };
const drawerAvatar = { width: 44, height: 44, borderRadius: 12, background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800 };
const drawerCloseBtn = { width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' };
const drawerBody = { padding: '24px', flex: 1, overflowY: 'auto' };
const drawerSection = { marginBottom: 32 };
const sectionTitle = { fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 };
const controlCard = { background: '#f8fafc', padding: 18, borderRadius: 16, border: '1px solid #e2e8f0' };
const statusRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const statusBtn = { border: 'none', padding: '8px 18px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const permissionsContainer = { display: 'flex', flexDirection: 'column', gap: 12 };
const permissionCard = { padding: '16px 20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14, transition: 'all 0.2s' };
const roleNameTag = { fontSize: 14, fontWeight: 800, color: '#1e293b' };
const siteMainLabel = { fontSize: 12, color: '#6366f1', fontWeight: 600, marginTop: 4 };
const revokeBtn = { background: '#fff', border: '1.5px solid #fee2e2', color: '#ef4444', cursor: 'pointer', padding: 8, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const detailBtn = { background: '#fff', border: '1.5px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: '#64748b', padding: '6px 12px', borderRadius: 8, cursor: 'pointer' };
const siteDetailBox = { marginTop: 14, padding: 14, background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 10, animation: 'fadeIn 0.2s' };
const detailItem = { marginBottom: 6, fontSize: 12, color: '#475569', display: 'flex', justifyContent: 'space-between' };
const drawerSearchWrapper = { background: '#f1f5f9', padding: '6px 12px', borderRadius: 10, display: 'flex', alignItems: 'center' };
const drawerSearchInput = { background: 'none', border: 'none', fontSize: 12, outline: 'none', color: '#1e293b', width: 100 };
const emptyPermissions = { padding: 40, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 14, fontSize: 13, border: '1px dashed #e2e8f0' };

const inlineConfirmWrapper = { display: 'flex', alignItems: 'center', gap: '12px', width: '100%', flexWrap: 'wrap' };
const confirmLabel = { fontWeight: 700, fontSize: 13, color: '#1e293b' };
const confirmText = { fontSize: '12px', fontWeight: '700', color: '#475569' };
const btnYes = { background: '#6366f1', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' };
const btnNo = { background: '#fff', color: '#64748b', border: '1.5px solid #e2e8f0', padding: '8px 16px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' };

const confirmIconStyle = { width: 60, height: 60, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' };
const detailLabelStyle = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' };
const detailValueStyle = { fontSize: 13, fontWeight: 600, color: '#1f2937' };
const pendingBannerStyle = { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: 12, display: 'flex', gap: 12, alignItems: 'flex-start' };
const approveBtnLargeStyle = { padding: '12px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', transition: 'all 0.2s' };
const warningCardStyle = { fontSize: 12, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 10 };
const deleteUserBtnStyle = { background: '#ef4444', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700 };

const getBadgeStyle = (user) => {
  const base = { padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'uppercase' };
  if (isPendingUser(user)) return { ...base, background: '#fffbeb', color: '#92400e' };
  if (isRejectedUser(user)) return { ...base, background: '#fee2e2', color: '#b91c1c' };
  if (user.isActive) return { ...base, background: '#dcfce7', color: '#166534' };
  return { ...base, background: '#fee2e2', color: '#991b1b' };
};